import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UUID } from 'angular2-uuid';
import { config } from '../../config';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ErrorService } from './error.service';
import { RouteService } from './route.service';
import { StoreService } from './store.service';
import { LoaderService } from './loader.service';
import { CustomerService } from './customer.service';
import { FirmStore } from '../modules/firm/store/firm.store';
import { DeliveryMethodService } from './delivery-method.service';
import { DeliveryMethods } from '../constants/delivery_methods';
import { TableDeliveryMethodStore } from '../modules/delivery-method/store/table-delivery-method.store';
import { DeliveryTimeStore } from '../modules/delivery-time/store/delivery-time.store';
import * as moment from 'moment';
import { DeliveryTimeServiceInterface } from '../modules/delivery-time/services/delivery-time-service.interface';
import { CustomerStore } from '../modules/customer/store/customer.store';
import { PickupPointStore } from '../modules/delivery-method/store/pickup-point.store';
import { Units } from '../constants/units';
import { BasketItemsService } from './basket-items.service';

@Injectable()
export class OrderService {
  endpoint_calculate: any = '/order/calculate';
  endpoint_confirm: any = '/order/calculate?confirm=1';
  endpoint_calculate_order: any = '/order/calculate_delivery';
  endpoint_addorder: any = '/order';
  endpoint_orders: any = '/orders/';
  endpoint_pending = '/orders/pending';
  endpoint_past = '/orders/past';
  endpoint_status = '/payment_status';
  endpoint_order_detail = '/order_detail/';
  endpoint_coupon = '/coupon';

  token: any;
  uuid: any;

  deliveryTimeService: DeliveryTimeServiceInterface;

  constructor(
    private http: HttpClient,
    public auth: AuthService,
    public router: Router,
    public routeService: RouteService,
    public errorService: ErrorService,
    public storeService: StoreService,
    private loader: LoaderService,
    private customerService: CustomerService,
    private firmStore: FirmStore,
    private deliveryMethodService: DeliveryMethodService,
    private tableDeliveryStore: TableDeliveryMethodStore,
    private deliveryTimeStore: DeliveryTimeStore,
    private customerStore: CustomerStore,
    private pickupPointStore: PickupPointStore,
    private basketItemsService: BasketItemsService
  ) {}

  /* Get order (session) */
  async getOrder() {
    return JSON.parse(localStorage.getItem('order'));
  }

  /* Save order (session) */
  saveOrder(new_order) {
    localStorage.setItem('order', JSON.stringify(new_order));
  }

  async getFirstAvailableTimestampForOrder(order, custId): Promise<any> {
    const firm = this.firmStore.currentFirm;
    const language = this.firmStore.language;
    const ownDomain = this.firmStore.ownDomain;
    const distributor = this.firmStore.distributor;

    await this.getUuid();

    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        let httpOptions;

        if (this.uuid != undefined && this.uuid.firm_id == firm.id) {
          httpOptions = {
            headers: new HttpHeaders({
              storeId: await this.storeService.getStoreId(),
              passwordToken: await this.checkToken(firm.id),
              demoPasswordToken: await this.getDemoToken(),
              firmId: String(firm.id),
              language: String(language),
              Accept: 'application/json',
              Authorization: 'Bearer ' + (await localStorage.getItem('token')),
              appVersion: config.VERSION,
              apiVersion: config.VERSION,
              deviceOS: 'Web',
              deviceOSVersion: 'Web',
              deviceModel: 'Web',
              customerId: String(custId),
              uuid: String(this.uuid),
              customerToken: await JSON.parse(localStorage.getItem('customerToken')),
            }),
          };
        } else {
          httpOptions = {
            headers: new HttpHeaders({
              firmId: String(firm.id),
              language: String(language),
              Accept: 'application/json',
              Authorization: 'Bearer ' + (await localStorage.getItem('token')),
              appVersion: config.VERSION,
              apiVersion: config.VERSION,
              deviceOS: 'Web',
              deviceOSVersion: 'Web',
              deviceModel: 'Web',
              customerId: String(custId),
              customerToken: await JSON.parse(localStorage.getItem('customerToken')),
            }),
          };
        }

        const body: any = {
          categories: order.categories,
          delivery_method: order.delivery_method,
          products: order.products,
          timestamp: order.timestamp,
        };

        if (order.firm_pickup_point_id) {
          body.firm_pickup_point_id = order.firm_pickup_point_id;
        }

        this.http.post(environment.API_URL + this.endpoint_calculate, body, httpOptions).subscribe(
          async (data: any) => {
            resolve(data);
          },
          async (err) => {
            const check = await this.errorService.checkCalculateErrors(
              err.status,
              await this.parseError(err),
              distributor,
              firm,
              ownDomain,
              language
            );

            if (check == 'TOKEN_INVALID') {
              this.getFirstAvailableTimestampForOrder(order, custId);
            }
          }
        );
      });
    }
  }

  /* Validate time */
  async validateTimestampForOrder(order, custId): Promise<any> {
    const firm = this.firmStore.currentFirm;
    const language = this.firmStore.language;
    const ownDomain = this.firmStore.ownDomain;
    const distributor = this.firmStore.distributor;

    await this.getUuid();

    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            passwordToken: await this.checkToken(firm.id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm.id),
            language: String(language),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(custId),
            uuid: String(this.uuid),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        this.http.post(environment.API_URL + this.endpoint_confirm, order, httpOptions).subscribe(
          (data: any) => {
            resolve(data);
          },
          async (err) => {
            const check = await this.errorService.checkCalculateErrors(
              err.status,
              await this.parseError(err),
              distributor,
              firm,
              ownDomain,
              language
            );

            if (check == 'TOKEN_INVALID') {
              this.validateTimestampForOrder(order, custId);
            }
          }
        );
      });
    }
  }

  /* Calculate delivery cost of order */
  async calculateOrder(firm, cust_id, order, lang_code, distributor, own_domain) {
    await this.getUuid();

    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        let httpOptions;

        if (this.uuid != undefined && this.uuid.firm_id == firm.id) {
          httpOptions = {
            headers: new HttpHeaders({
              storeId: await this.storeService.getStoreId(),
              passwordToken: await this.checkToken(firm.id),
              demoPasswordToken: await this.getDemoToken(),
              firmId: String(firm.id),
              language: String(lang_code),
              Accept: 'application/json',
              Authorization: 'Bearer ' + (await localStorage.getItem('token')),
              appVersion: config.VERSION,
              apiVersion: config.VERSION,
              deviceOS: 'Web',
              deviceOSVersion: 'Web',
              deviceModel: 'Web',
              customerId: String(cust_id),
              uuid: String(this.uuid),
              customerToken: await JSON.parse(localStorage.getItem('customerToken')),
            }),
          };
        } else {
          httpOptions = {
            headers: new HttpHeaders({
              firmId: String(firm.id),
              language: String(lang_code),
              Accept: 'application/json',
              Authorization: 'Bearer ' + (await localStorage.getItem('token')),
              appVersion: config.VERSION,
              apiVersion: config.VERSION,
              deviceOS: 'Web',
              deviceOSVersion: 'Web',
              deviceModel: 'Web',
              customerId: String(cust_id),
              customerToken: await JSON.parse(localStorage.getItem('customerToken')),
            }),
          };
        }

        if ((this.uuid != undefined || this.uuid != null) && this.uuid.firm_id == firm.id) {
          httpOptions.headers.append('uuid', String(this.uuid));
        }

        this.http.post(environment.API_URL + this.endpoint_calculate_order, order, httpOptions).subscribe(
          async (data: any) => {
            resolve(data);
          },
          async (err) => {
            const check = await this.errorService.checkCalculateErrors(
              err.status,
              await this.parseError(err),
              distributor,
              firm,
              own_domain,
              lang_code
            );

            if (check == 'TOKEN_INVALID') {
              this.calculateOrder(firm, cust_id, order, lang_code, distributor, own_domain);
            } else if (
              check === 'DELIVERY_DISTANCE_TOO_FAR' ||
              check === 'ERROR_CALCULATING_DELIVERY_DISTANCE' ||
              check === 'DELIVERY_ZIPCODE_INVALID_FOR_FIRM' ||
              check === 'ORDER_PRICE_TOO_LOW_FOR_DELIVERY'
            ) {
              resolve(err);
            }
          }
        );
      });
    }
  }

  /* Add order to server */
  async add(firm, order, customer_id, lang_code, own_domain, isFree?) {
    await this.getUuid();

    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        let store_id;
        await config.STORE_IDS.forEach((id) => {
          if (id.domain == location.hostname) {
            store_id = id.store_id;
          }
        });

        const httpOptions = {
          headers: new HttpHeaders({
            passwordToken: await this.checkToken(firm.id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm.id),
            language: String(lang_code),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(customer_id),
            uuid: String(this.uuid),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        if (store_id) {
          httpOptions.headers.append('storeId', store_id);
        }

        const orderStr = JSON.stringify(order);
        const body = JSON.parse(orderStr);

        if (body.products.length == 0) {
          body.products = undefined;
        }

        body.coupon = undefined;

        if (isFree) {
          if (isFree == true) {
            body.payment_method_id = 1;
          }
        }

        this.http.post(environment.API_URL + this.endpoint_addorder, body, httpOptions).subscribe(
          (data) => {
            resolve(data);
          },
          async (err) => {
            const error = await this.parseError(err);

            if (error.errors) {
              if (error.errors.error == 'SHOP_OFFLINE') {
                err.error.type = 'shop_offline';
                resolve(error);
              } else {
                const check = await this.errorService.checkOrderErrors(err.status, error, firm, own_domain, lang_code);

                if (check == 'TOKEN_INVALID') {
                  await this.add(firm, order, customer_id, lang_code, own_domain, isFree);
                } else if (check == 'BASKET_CHANGE') {
                  err.error.type = 'basket_change';
                  resolve(err.error);
                } else if (error === 'INVALID_UUID') {
                  await this.setUuid();
                  await this.add(firm, order, customer_id, lang_code, own_domain, isFree);
                }
              }
            } else {
              const check = await this.errorService.checkOrderErrors(err.status, error, firm, own_domain, lang_code);

              if (check == 'TOKEN_INVALID') {
                this.add(firm, order, customer_id, lang_code, own_domain, isFree);
              } else if (check == 'BASKET_CHANGE') {
                err.error.type = 'basket_change';
                resolve(err.error);
              }
            }
          }
        );
      });
    }
  }

  /* Get order from server */
  async get(firm_id, order_id, customer_id, lang_code) {
    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            passwordToken: await this.checkToken(firm_id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm_id),
            language: String(lang_code),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(customer_id),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        this.http.get(environment.API_URL + this.endpoint_orders + order_id, httpOptions).subscribe(
          (data: any) => {
            resolve(data.data);
          },
          async (err) => {
            resolve('ERROR');
          }
        );
      });
    }
  }

  /* Get order from server */
  async getStatus(firm_id, order_id, customer_id, lang_code) {
    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            passwordToken: await this.checkToken(firm_id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm_id),
            language: String(lang_code),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(customer_id),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        this.http
          .get(environment.API_URL + this.endpoint_orders + order_id + this.endpoint_status, httpOptions)
          .subscribe(
            (data: any) => {
              resolve(data);
            },
            async (err) => {
              resolve('ERROR');
            }
          );
      });
    }
  }

  /* Delete order from server */
  async getPendingOrders(firm, customer_id, lang_code, own_domain) {
    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            passwordToken: await this.checkToken(firm.id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm.id),
            language: String(lang_code),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(customer_id),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        this.http.get(environment.API_URL + this.endpoint_pending, httpOptions).subscribe(
          (data: any) => {
            resolve(data.data);
          },
          async (err) => {
            const check = await this.errorService.checkOrderErrors(
              err.status,
              await this.parseError(err),
              firm,
              own_domain,
              lang_code
            );

            if (check == 'TOKEN_INVALID') {
              this.getPendingOrders(firm, customer_id, lang_code, own_domain);
            }
          }
        );
      });
    }
  }

  /* Delete order from server */
  async getPastOrders(firm, customer_id, lang_code, own_domain) {
    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            passwordToken: await this.checkToken(firm.id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm.id),
            language: String(lang_code),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(customer_id),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        this.http.get(environment.API_URL + this.endpoint_past, httpOptions).subscribe(
          (data: any) => {
            resolve(data.data);
          },
          async (err) => {
            const check = await this.errorService.checkOrderErrors(
              err.status,
              await this.parseError(err),
              firm,
              own_domain,
              lang_code
            );

            if (check == 'TOKEN_INVALID') {
              this.getPastOrders(firm, customer_id, lang_code, own_domain);
            }
          }
        );
      });
    }
  }

  /* Get order by reference */
  async getOrderByReference(reference) {
    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
          }),
        };

        this.http.get(environment.API_URL + this.endpoint_order_detail + reference, httpOptions).subscribe(
          (data: any) => {
            resolve(data.data);
          },
          async (err) => {
            resolve('ERROR');
          }
        );
      });
    }
  }

  /* check coupon */
  async checkCoupon(firm_id, body, customer_id, lang_code) {
    await this.getUuid();

    if (this.errorService.checkConnection()) {
      return new Promise(async (resolve) => {
        this.token = await localStorage.getItem('token');
        if (!this.token) {
          this.token = await this.auth.getToken();
        }

        const httpOptions = {
          headers: new HttpHeaders({
            storeId: await this.storeService.getStoreId(),
            passwordToken: await this.checkToken(firm_id),
            demoPasswordToken: await this.getDemoToken(),
            firmId: String(firm_id),
            language: String(lang_code),
            Accept: 'application/json',
            Authorization: 'Bearer ' + (await localStorage.getItem('token')),
            appVersion: config.VERSION,
            apiVersion: config.VERSION,
            deviceOS: 'Web',
            deviceOSVersion: 'Web',
            deviceModel: 'Web',
            customerId: String(customer_id),
            uuid: String(this.uuid),
            customerToken: await JSON.parse(localStorage.getItem('customerToken')),
          }),
        };

        this.http.post(environment.API_URL + this.endpoint_coupon, body, httpOptions).subscribe(
          (data) => {
            resolve(data);
          },
          async (err) => {
            const error = await this.parseError(err);

            const check = await this.errorService.checkCouponErrors(err.status, error);

            if (check == 'TOKEN_INVALID') {
              this.checkCoupon(firm_id, body, customer_id, lang_code);
            } else if (check == 'ORDER_PRICE_TOO_LOW_FOR_COUPON') {
              resolve(error);
            }
          }
        );
      });
    }
  }

  /* Functions */
  async getUuid() {
    const uuid_str = await localStorage.getItem('uuid');
    if (uuid_str != null) {
      this.uuid = JSON.parse(uuid_str);
    }
  }

  async setUuid() {
    await localStorage.setItem('uuid', JSON.stringify(UUID.UUID()));
  }

  parseError(err) {
    try {
      return JSON.parse(err.error);
    } catch (error) {
      error = err.error;
      return err.error;
    }
  }

  async checkToken(firm_id) {
    const tokens = await JSON.parse(localStorage.getItem('password_tokens'));

    let passwordToken = [];

    if (tokens != null) {
      tokens.forEach((token) => {
        if (token.firm_id == firm_id) {
          passwordToken = token.passwordToken;
        }
      });
    }

    return passwordToken;
  }

  async getDemoToken() {
    let demoToken = await JSON.parse(localStorage.getItem('demo_password_token'));

    if (demoToken == null) {
      demoToken = '';
    }

    return demoToken;
  }

  async navigateToCheckout(method, firm, own_domain, language) {
    let user;

    if (method === 'guest') {
      user = await this.customerService.loginAsGuest();
      await this.saveToOrder(method, user, firm);
    } else if (method === 'user') {
      this.loader.present();
      const login = await JSON.parse(localStorage.getItem('login'));
      if (login) {
        await this.customerService.linkFirm(firm, login.id, language, firm.distributor, own_domain);
        user = await this.customerService.get(firm, login.id, language, firm.distributor);
        await this.saveToOrder(method, user, firm);
      }
      this.loader.dismiss();
    }

    // Check user info
    if (!user.first_name || !user.last_name || !user.phone || !user.email) {
      this.routeService.navigateToExtraInfo(method, own_domain, firm, language);
    } else {
      this.routeService.navigateToPaymentMethod(own_domain, firm, language);
    }
  }

  async saveToOrder(method, user, firm) {
    const order: any = {
      discount_percentage: 0,
      user: {
        id: user.id,
      },
    };

    if (method === 'user') {
      order.isGuest = false;
      order.user.first_name = user.first_name;
      order.user.last_name = user.last_name;
      order.user.phone = user.phone;
      order.user.company = user.company;

      if (firm.use_loyalty) {
        if (user.loyalty) {
          if (user.loyalty.add_loyalty_to_order == true) {
            order.add_loyalty_to_order = true;
          } else {
            order.add_loyalty_to_order = false;
          }
        } else {
          order.add_loyalty_to_order = false;
        }
      } else {
        order.add_loyalty_to_order = false;
      }
    } else if (method === 'guest') {
      order.isGuest = true;
      order.add_loyalty_to_order = false;

      const user: any = await this.customerService.loginAsGuest();
      order.user = {
        id: user.id,
      };
    }

    this.setUuid();

    await this.setDeliveryMethodToOrder(order);
    await this.setProducts(order, firm);

    this.saveOrder(order);
  }

  async setProducts(order, firm) {
    const basketItems = await this.basketItemsService.get(firm.id);

    // set products
    const products = [];
    basketItems.forEach((item) => {
      const ingredients = [];
      const product_options = [];

      // set ingredients
      item.item.ingredients.forEach((ingr) => {
        const ingredient = {
          id: ingr.id,
          type: ingr.type,
          price: ingr.price,
          quantity: ingr.amount,
          action: ingr.action,
          selected: ingr.selected,
        };
        ingredients.push(ingredient);
      });

      // set product options
      if (item.item.options_selected) {
        item.item.options_selected.forEach((option) => {
          const product_option = {
            group_id: option.id,
            id: option.selected_option.id,
            price: option.selected_option.price,
          };
          product_options.push(product_option);
        });
      }

      // Parse init item
      const init_item = JSON.parse(item.item.init_item);

      // set product
      const product: any = {
        id: init_item.id,
        parent_cat_id: init_item.category.parent_category_id,
        cat_id: init_item.category.category_id,
        quantity: item.item.amount,
        ingredients: ingredients,
        product_options: product_options,
        comment: item.item.comment,
        unit_id: item.item.unit_id,
      };

      if (item.item.unit_id === Units.KG) {
        product.quantity = item.item.amount / 1000;
      }

      if (init_item.weight != null) {
        product.weight = init_item.weight;
      }

      if (firm.uses_vat) {
        product.vat_percentage = item.item.item.vat_percentage;
      }

      // Set price of product
      if (item.item.item.has_promotional_price) {
        product.price = init_item.promotion.price;
      } else {
        product.price = init_item.price;
      }

      products.push(product);
    });

    order.products = products;
  }

  async setDeliveryMethodToOrder(order) {
    const deliveryMethod = await this.deliveryMethodService.getSelectedDeliveryMethod();
    order.delivery_method = deliveryMethod;

    // Delivery
    if (deliveryMethod === DeliveryMethods.Delivery) {
      const selectedAddress = this.customerStore.getSelectedAddress();
      order.delivery_street = selectedAddress?.street;
      order.delivery_street_nr = selectedAddress?.bus
        ? `${selectedAddress?.house_number} ${selectedAddress?.bus}`
        : selectedAddress?.house_number;
      order.delivery_zipcode = selectedAddress?.zip_code;
      order.delivery_locality = selectedAddress?.locality;
      order.delivery_country_id = selectedAddress?.country?.id;
    }

    // Table
    if (deliveryMethod === DeliveryMethods.Table) {
      const selectedTableNumber = this.tableDeliveryStore.getSelectedTableNumber();
      const selectedTableMethod = this.tableDeliveryStore.getSelectedTableMethod();

      order.process_timestamp = moment().format();
      order.table_number = selectedTableNumber;

      if (selectedTableMethod && selectedTableMethod.id !== 0) {
        order.custom_table_method_id = selectedTableMethod.id;
      }
    } else {
      order.table_number = null;
      order.custom_table_method_id = null;
    }

    // Pickup point
    if (deliveryMethod === DeliveryMethods.PickupPoint) {
      const selectedPickupPoint = await this.pickupPointStore.getSelectedPickupPoint();
      order.firm_pickup_point_id = selectedPickupPoint.id;
      order.delivery_method = DeliveryMethods.Pickup;
    } else {
      order.firm_pickup_point_id = null;
    }
  }

  async setProcessTimeToOrder(order, deliveryTimeService) {
    const deliveryTime = await deliveryTimeService?.getDeliveryTime();

    if (deliveryTime === 'asap') {
      const firstAvailable = await deliveryTimeService?.calculateTimestamp();
      order.asap = true;
      order.process_timestamp = firstAvailable.timestamp;
    } else if (deliveryTime) {
      order.asap = undefined;
      order.process_timestamp = deliveryTime;

      const timeslotId = this.deliveryTimeStore.getTimeslot();
      if (timeslotId) {
        order.timeslot_id = timeslotId;
      }
    }
  }
}
